import React, { useEffect, useState } from 'react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Download, CheckCircle2, Search, RefreshCcw } from 'lucide-react';
import { PaymentsOverview } from '@/components/admin/PaymentsOverview';
import { api, downloadFile } from '@/lib/api';
import { formatINR, formatNumber, formatDateTime } from '@/lib/format';
import { exportToCsv } from '@/lib/csv';
import { toast } from 'sonner';

const INVOICEABLE = ['paid', 'completed', 'pending', 'paid_bridge_failed'];
const FULFILLABLE = ['created', 'paid', 'pending', 'paid_bridge_failed'];
const STATUS_OPTIONS = ['created', 'paid', 'pending', 'completed', 'paid_bridge_failed', 'failed'];

export default function AdminPaymentsPage() {
    const [payments, setPayments] = useState([]);
    const [platform, setPlatform] = useState('all');
    const [status, setStatus] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [search, setSearch] = useState('');
    const [fulfillTarget, setFulfillTarget] = useState(null);
    const [fulfillDiamonds, setFulfillDiamonds] = useState('');
    const [fulfillNote, setFulfillNote] = useState('');
    const [busy, setBusy] = useState(false);

    const load = () => {
        const p = new URLSearchParams();
        if (platform !== 'all') p.set('platform', platform);
        if (status !== 'all') p.set('status', status);
        if (startDate) p.set('start_date', startDate);
        if (endDate) p.set('end_date', endDate);
        if (search.trim()) p.set('q_search', search.trim());
        api.get(`/admin/payments?${p.toString()}`).then(r => setPayments(r.data.payments || []));
    };
    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
    }, [platform, status, startDate, endDate, search]);

    const getInvoice = async (paymentId) => {
        try { await downloadFile(`/admin/invoice/${paymentId}`, `invoice-${paymentId}.pdf`); }
        catch (e) { toast.error(e.response?.status === 404 ? 'Invoice not generated for this payment' : 'Could not download invoice'); }
    };

    const [rechecking, setRechecking] = useState('');
    const recheck = async (p) => {
        setRechecking(p.id);
        try {
            const { data } = await api.post(`/recharge/admin/payments/${p.id}/recheck`);
            const msg = {
                fulfilled: `Payment was captured — recharge processed (${(data.payment_status || '').replace(/_/g, ' ')})`,
                not_captured: `Gateway shows this order is NOT captured — no recharge processed.${data.gateway_error ? ` Reason: ${data.gateway_error.description || data.gateway_error.reason}` : ''}`,
                already: `Already processed (${data.payment_status})`,
                duplicate: 'This gateway payment is already linked to another order — skipped.',
                skipped: 'Gateway not configured / mock order — nothing to check.',
                error: 'Could not reach the gateway right now. Try again shortly.',
            }[data.result] || data.result;
            (data.result === 'fulfilled' ? toast.success : toast.message)(msg);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Recheck failed'); }
        finally { setRechecking(''); }
    };

    const openFulfill = (p) => { setFulfillTarget(p); setFulfillDiamonds(String(p.diamonds_or_coins || '')); setFulfillNote(''); };

    const changeStatus = async (p, newStatus) => {
        if (newStatus === p.status) return;
        try {
            await api.patch(`/admin/payments/${p.id}/status`, { status: newStatus });
            toast.success(`Status changed to ${newStatus.replace(/_/g, ' ')}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not change status'); }
    };

    const submitFulfill = async () => {
        if (!fulfillTarget) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/payments/${fulfillTarget.id}/fulfill`, {
                diamonds: fulfillDiamonds ? Number(fulfillDiamonds) : null,
                note: fulfillNote || null,
            });
            const st = data.payment_status;
            if (st === 'completed') toast.success(`Recharge delivered · Invoice ${data.invoice_number || ''}`);
            else if (st === 'pending') toast.message('Payment recorded — delivery is pending/manual. Check Recharge Logs.');
            else toast.error(`Payment recorded but upstream delivery FAILED (${data.bridge_status || 'error'}). Fix the platform session and retry.`);
            setFulfillTarget(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not fulfill payment'); }
        finally { setBusy(false); }
    };

    const exportCsv = () => {
        exportToCsv('payments', payments, [
            { header: 'Time', accessor: (p) => formatDateTime(p.created_at) },
            { header: 'User', accessor: (p) => p.user_name },
            { header: 'Email', accessor: (p) => p.user_email },
            { header: 'WhatsApp', accessor: (p) => p.user_whatsapp },
            { header: 'Platform', accessor: (p) => p.platform },
            { header: 'In-app ID', accessor: (p) => p.in_app_id },
            { header: 'Amount (INR)', accessor: (p) => p.amount_inr },
            { header: 'Units', accessor: (p) => p.diamonds_or_coins },
            { header: 'Razorpay Order', accessor: (p) => p.razorpay_order_id },
            { header: 'Status', accessor: (p) => p.status },
            { header: 'Gateway', accessor: (p) => p.gateway || 'razorpay' },
            { header: 'IP', accessor: (p) => p.client_ip || '' },
            { header: 'Browser', accessor: (p) => (p.client_browser ? `${p.client_browser} / ${p.client_os || ''}` : '') },
            { header: 'Auto-recovered', accessor: (p) => (p.reconciled ? 'yes' : '') },
            { header: 'Gateway error', accessor: (p) => (p.gateway_error ? `${p.gateway_error.description || ''} ${p.gateway_error.reason || ''}`.trim() : '') },
            { header: 'Fulfilled by admin', accessor: (p) => (p.fulfilled_by_admin ? p.fulfilled_by || 'yes' : '') },
        ]);
    };

    return (
        <AdminShell title="Payments" actions={<Button variant="outline" size="sm" onClick={exportCsv} data-testid="admin-payments-export-csv"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>}>
            <PaymentsOverview />
            <Card className="p-4 sm:p-5">
                <div className="flex flex-wrap gap-3 mb-3 items-center">
                    <div className="relative w-full sm:w-72">
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name / email / WhatsApp / in-app ID…" className="h-9 pl-8" data-testid="admin-payments-search" />
                        <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    </div>
                    <Select value={platform} onValueChange={setPlatform}><SelectTrigger className="w-40" data-testid="admin-payments-platform-filter"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="all">All platforms</SelectItem>{['olaparty','trumeet'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select>
                    <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44" data-testid="admin-payments-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>{['all','created','paid','completed','pending','paid_bridge_failed','failed'].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent></Select>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        From <Input type="datetime-local" value={startDate} max={endDate || undefined} onChange={e => setStartDate(e.target.value)} className="h-9 w-52" data-testid="admin-payments-start-date" />
                        To <Input type="datetime-local" value={endDate} min={startDate || undefined} onChange={e => setEndDate(e.target.value)} className="h-9 w-52" data-testid="admin-payments-end-date" />
                        {(startDate || endDate) && <Button variant="ghost" size="sm" onClick={() => { setStartDate(''); setEndDate(''); }} data-testid="admin-payments-clear-dates">Clear</Button>}
                    </div>
                </div>
                <div className="overflow-x-auto" data-testid="admin-payments-table">
                    <Table>
                        <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>User</TableHead><TableHead>Platform</TableHead><TableHead>In-app ID</TableHead><TableHead>Amount</TableHead><TableHead>Units</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {payments.map(p => (
                                <TableRow key={p.id}>
                                    <TableCell className="whitespace-nowrap">{formatDateTime(p.created_at)}</TableCell>
                                    <TableCell>
                                        <div className="font-medium">{p.user_name || '—'}</div>
                                        <div className="text-xs text-muted-foreground">{p.user_email}</div>
                                        {p.user_whatsapp && <div className="text-xs text-muted-foreground font-mono">{p.user_whatsapp}</div>}
                                        {p.fulfilled_by_admin && <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Fulfilled by admin</span>}
                                        {p.reconciled && <span className="inline-block mt-0.5 ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`admin-payment-reconciled-${p.id}`}>Auto-recovered</span>}
                                        {p.gateway_error && !['paid', 'completed', 'pending'].includes(p.status) && <div className="text-[10px] text-rose-700 max-w-[220px]" title={JSON.stringify(p.gateway_error)} data-testid={`admin-payment-gw-error-${p.id}`}>Gateway: {p.gateway_error.description || p.gateway_error.reason}{p.gateway_error.source ? ` (${p.gateway_error.source})` : ''}</div>}
                                        {p.client_ip && <div className="text-[10px] text-muted-foreground font-mono" data-testid={`admin-payment-ip-${p.id}`}>IP {p.client_ip}{p.client_browser ? ` · ${p.client_browser}` : ''}</div>}
                                    </TableCell>
                                    <TableCell className="capitalize">{p.platform}</TableCell>
                                    <TableCell className="font-mono text-xs">{p.in_app_id}</TableCell>
                                    <TableCell>{formatINR(p.amount_inr)}</TableCell>
                                    <TableCell className="tabular-nums">{formatNumber(p.diamonds_or_coins)}</TableCell>
                                    <TableCell><StatusBadge status={p.status} /></TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1.5">
                                            {['created', 'failed'].includes(p.status) && !p.fulfilled_by_admin && (
                                                <Button size="sm" variant="outline" disabled={rechecking === p.id} onClick={() => recheck(p)} title="Re-check with the payment gateway" data-testid={`admin-recheck-${p.id}`}>
                                                    <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${rechecking === p.id ? 'animate-spin' : ''}`} /> Recheck
                                                </Button>
                                            )}
                                            {FULFILLABLE.includes(p.status) && !p.fulfilled_by_admin && (
                                                <Button size="sm" variant="outline" className="text-blue-700 border-blue-200" onClick={() => openFulfill(p)} data-testid={`admin-fulfill-${p.id}`}>
                                                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark fulfilled
                                                </Button>
                                            )}
                                            {INVOICEABLE.includes(p.status) && (
                                                <Button size="sm" variant="outline" onClick={() => getInvoice(p.id)} data-testid={`admin-invoice-download-${p.id}`}>
                                                    <Download className="h-3.5 w-3.5 mr-1" /> PDF
                                                </Button>
                                            )}
                                            <Select value={p.status} onValueChange={(v) => changeStatus(p, v)}>
                                                <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`admin-payment-status-select-${p.id}`}><SelectValue /></SelectTrigger>
                                                <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} className="capitalize text-xs">{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {payments.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No payments.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <Dialog open={!!fulfillTarget} onOpenChange={(o) => !o && setFulfillTarget(null)}>
                <DialogContent data-testid="admin-fulfill-dialog">
                    <DialogHeader><DialogTitle>Mark payment as fulfilled</DialogTitle></DialogHeader>
                    <div className="text-sm space-y-3">
                        <p className="text-muted-foreground">Confirm the customer's payment actually succeeded (check your Razorpay dashboard for this order) and that you have manually transferred the diamonds/coins to <span className="font-medium text-foreground">{fulfillTarget?.in_app_id}</span> on {fulfillTarget?.platform}. This marks the payment successful and generates an invoice.</p>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Diamonds / coins transferred</Label>
                            <Input type="number" value={fulfillDiamonds} onChange={e => setFulfillDiamonds(e.target.value)} data-testid="admin-fulfill-diamonds" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Note (optional)</Label>
                            <Input value={fulfillNote} onChange={e => setFulfillNote(e.target.value)} placeholder="e.g. transferred manually via agency panel" data-testid="admin-fulfill-note" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFulfillTarget(null)}>Cancel</Button>
                        <Button onClick={submitFulfill} disabled={busy} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="admin-fulfill-confirm">{busy ? 'Saving…' : 'Confirm fulfilled'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminShell>
    );
}
